import { type CSSProperties, useRef, useState, useEffect } from 'react'
import Draggable, {
  type DraggableData,
  type DraggableEvent,
} from 'react-draggable'
import { ResizableBox, type ResizeCallbackData } from 'react-resizable'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { theme } from '@/lib/theme'
import 'react-resizable/css/styles.css'

interface TiledBounds {
  x: number
  y: number
  width: number
  height: number
  minimizeType?: 'vertical' | 'horizontal'
}

interface WindowProps {
  id: number
  title: string
  children?: React.ReactNode
  isActive: boolean
  onFocus: () => void
  onClose: () => void
  zIndex: number
  initialPosition: { x: number; y: number }
  initialSize?: { width: number | string; height: number | string }
  draggable?: boolean
  isMobile?: boolean
  isMinimized?: boolean
  isMaximized?: boolean
  onMinimize?: () => void
  onMaximize?: () => void
  tiledBounds?: TiledBounds // When provided, window uses tiled layout
  useTiledMode?: boolean // Enable tiled mode globally
  titleBarActions?: React.ReactNode // Custom UI elements to render in title bar
  onReorderDragStart?: (windowId: number) => void
  onReorderDragOver?: (windowId: number) => void
  onReorderDrop?: (windowId: number) => void
  onReorderDragEnd?: () => void
  isDraggedForReorder?: boolean
  isDragOverForReorder?: boolean
  windowCount?: number // Total number of windows
  canMaximize?: boolean // Whether maximize is allowed
  contentType?: string // For data attributes / targeting
  enableReorderDrag?: boolean // Whether header/minimized bar can be used to reorder in tiled mode
}

export default function Window({
  id,
  title,
  children,
  isActive,
  onFocus,
  onClose,
  zIndex,
  initialPosition,
  initialSize = { width: 550, height: 300 },
  draggable = true,
  isMobile = false,
  isMinimized = false,
  isMaximized = false,
  onMinimize,
  onMaximize,
  tiledBounds,
  useTiledMode = false,
  titleBarActions,
  onReorderDragStart,
  onReorderDragOver,
  onReorderDrop,
  onReorderDragEnd,
  isDraggedForReorder = false,
  isDragOverForReorder = false,
  windowCount = 1,
  canMaximize = true,
  contentType,
  enableReorderDrag = true,
}: WindowProps) {
  const { isDark, colors } = useThemeMode()
  const { i18n } = useTranslation()

  const draggableRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)

  // Calculate initial position and size together to avoid bounds issues
  const getInitialPositionAndSize = () => {
    const viewportWidth =
      typeof window !== 'undefined' ? window.innerWidth : 1200
    const viewportHeight =
      typeof window !== 'undefined' ? window.innerHeight : 800

    // Calculate constrained size first
    const requestedWidth =
      typeof initialSize.width === 'number'
        ? initialSize.width
        : parseInt(initialSize.width as string, 10)
    const requestedHeight =
      typeof initialSize.height === 'number'
        ? initialSize.height
        : parseInt(initialSize.height as string, 10)

    const maxWidth = Math.max(200, viewportWidth - 40) // 20px padding on each side
    const maxHeight = Math.max(150, viewportHeight - 40)

    const finalWidth = Math.min(requestedWidth, maxWidth)
    const finalHeight = Math.min(requestedHeight, maxHeight)

    // Now calculate position based on actual size
    let finalPosition = initialPosition

    if (!initialPosition) {
      // Define center region (middle 60% of screen)
      const centerRegionWidth = viewportWidth * 0.6
      const centerRegionHeight = viewportHeight * 0.6
      const centerStartX = (viewportWidth - centerRegionWidth) / 2
      const centerStartY = (viewportHeight - centerRegionHeight) / 2

      // Ensure window fits within the viewport completely
      const maxX = Math.max(0, viewportWidth - finalWidth)
      const maxY = Math.max(0, viewportHeight - finalHeight)

      // Calculate position within center region, but constrained to viewport
      const targetX =
        centerStartX +
        Math.random() * Math.max(0, centerRegionWidth - finalWidth)
      const targetY =
        centerStartY +
        Math.random() * Math.max(0, centerRegionHeight - finalHeight)

      // Clamp to viewport bounds
      const clampedX = Math.max(0, Math.min(maxX, targetX))
      const clampedY = Math.max(0, Math.min(maxY, targetY))

      finalPosition = {
        x: clampedX,
        y: clampedY,
      }
    }

    return {
      position: finalPosition,
      size: { width: finalWidth, height: finalHeight },
    }
  }

  const [positionAndSize, setPositionAndSize] = useState(() => {
    const result = getInitialPositionAndSize()
    return {
      position: result.position,
      size: result.size,
    }
  })

  const position = positionAndSize.position
  const size = positionAndSize.size

  const setPosition = (newPosition: { x: number; y: number }) => {
    setPositionAndSize((prev) => ({ ...prev, position: newPosition }))
  }

  const setSize = (newSize: { width: number; height: number }) => {
    setPositionAndSize((prev) => ({ ...prev, size: newSize }))
  }

  const handleResize = (
    _event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    // Ensure resize doesn't push window out of bounds
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let newWidth = data.size.width
    let newHeight = data.size.height

    // Clamp size to keep window within viewport
    newWidth = Math.min(newWidth, viewportWidth - position.x)
    newHeight = Math.min(newHeight, viewportHeight - position.y)

    setSize({ width: newWidth, height: newHeight })
  }

  const handleResizeStop = (
    _event: React.SyntheticEvent,
    data: ResizeCallbackData
  ) => {
    setIsResizing(false)

    // Ensure final size doesn't push window out of bounds
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let newWidth = data.size.width
    let newHeight = data.size.height

    // Clamp size to keep window within viewport
    newWidth = Math.min(newWidth, viewportWidth - position.x)
    newHeight = Math.min(newHeight, viewportHeight - position.y)

    setSize({ width: newWidth, height: newHeight })
  }

  const handleDragStart = (e: DraggableEvent) => {
    setIsDragging(true)
    onFocus()
  }

  const handleDrag = (e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y })
  }

  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    setIsDragging(false)

    let finalX = data.x
    let finalY = data.y

    // Ensure window stays completely within viewport bounds
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const windowWidth = size.width
    const windowHeight = size.height

    // Keep windows completely within viewport
    const maxX = viewportWidth - windowWidth
    const minX = 0
    const maxY = viewportHeight - windowHeight
    const minY = 0

    finalX = Math.max(minX, Math.min(maxX, finalX))
    finalY = Math.max(minY, Math.min(maxY, finalY))

    setPosition({ x: finalX, y: finalY })
  }

  // Calculate bounds for react-draggable - keep windows fully within viewport
  const calculateBounds = () => {
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const windowWidth = size.width
    const windowHeight = size.height

    return {
      left: 0,
      top: 0,
      right: viewportWidth - windowWidth,
      bottom: viewportHeight - windowHeight,
    }
  }

  // Determine if we're in tiled mode
  const isTiled = useTiledMode || !!tiledBounds

  // Use tiled bounds or fallback to position/size state
  const effectiveBounds = tiledBounds || {
    x: position.x,
    y: position.y,
    width: size.width,
    height: size.height,
  }

  // Determine minimize rendering
  const minimizeType = tiledBounds?.minimizeType
  const isMinimizedAndTiled = isMinimized && isTiled

  const windowStyle: CSSProperties = {
    position: isTiled || draggable ? 'absolute' : 'fixed',
    left: isTiled ? effectiveBounds.x : draggable ? undefined : '50%',
    top: isTiled ? effectiveBounds.y : draggable ? undefined : '50%',
    width: isMobile ? '100%' : isTiled ? effectiveBounds.width : size.width,
    height: isMobile
      ? '100%'
      : isTiled
        ? effectiveBounds.height
        : size.height,
    zIndex,
    backgroundColor: colors.windowBg,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    pointerEvents: 'auto',
    opacity: isDragging || isDraggedForReorder ? 0.5 : 1,
    transition: isTiled
      ? 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1), top 0.2s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s cubic-bezier(0.4, 0, 0.2, 1), height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
      : isDragging || isResizing
        ? 'none'
        : 'border 0.2s ease, box-shadow 0.2s ease',
    border: isMaximized
      ? `2px solid ${colors.primaryAccent}`
      : isActive
        ? `2px solid ${colors.windowBorderActive}`
        : `1px solid ${colors.windowBorder}`,
    outline: 'none', // Prevent any browser-default outline
    boxSizing: 'border-box', // Ensure border is included in dimensions
  }

  if (isMobile) {
    windowStyle.top = 0
    windowStyle.left = 0
    windowStyle.borderRadius = 0
    windowStyle.boxShadow = 'none'
    windowStyle.transition = 'none'
    if (!isActive) {
      windowStyle.display = 'none'
    }
  } else if (!isTiled) {
    windowStyle.left = draggable ? undefined : '50%'
    windowStyle.top = draggable ? undefined : '50%'
    windowStyle.transform = draggable ? undefined : 'translate(-50%, -50%)'
    windowStyle.borderRadius = '8px'
    windowStyle.boxShadow = isActive
      ? '0 20px 60px rgba(0, 0, 0, 0.12), 0 8px 20px rgba(0, 0, 0, 0.08)'
      : '0 4px 12px rgba(0, 0, 0, 0.06)'
    windowStyle.transition =
      isDragging || isResizing ? 'none' : theme.transitions.boxShadow
  } else {
    // Tiled mode styling
    windowStyle.borderRadius = '8px'
    windowStyle.boxShadow = isActive
      ? '0 20px 60px rgba(0, 0, 0, 0.12), 0 8px 20px rgba(0, 0, 0, 0.08)'
      : '0 4px 12px rgba(0, 0, 0, 0.06)'
  }

  // Minimized collapsed view
  const minimizedContent = (
    <div
      draggable={isTiled && !isMobile && enableReorderDrag}
      onDragStart={(e) => {
        if (isTiled && enableReorderDrag && onReorderDragStart) {
          const target = e.target as HTMLElement
          // Don't start a reorder drag when interacting with window controls
          // (including the chat text-size slider, which uses .window-control-button)
          if (target.closest('.window-control-button')) {
            e.preventDefault()
            e.stopPropagation()
            return
          }
          e.stopPropagation()
          onReorderDragStart(id)
        }
      }}
      onDragEnd={(e) => {
        if (isTiled && onReorderDragEnd) {
          e.stopPropagation()
          onReorderDragEnd()
        }
      }}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: minimizeType === 'vertical' ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.windowHeaderBg,
        borderRadius: '8px',
        padding: minimizeType === 'vertical' ? '12px 4px' : '8px 12px',
        gap: '8px',
        cursor:
          isTiled && enableReorderDrag
            ? isDraggedForReorder
              ? 'grabbing'
              : 'grab'
            : 'default',
        userSelect: 'none',
      }}
    >
      {/* Control buttons */}
      <div
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        style={{
          display: 'flex',
          flexDirection: minimizeType === 'vertical' ? 'column' : 'row',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
        }}
      >
        <div
          draggable={false}
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          style={{
            width: '10px',
            height: '10px',
            backgroundColor: colors.windowControlBg,
            border: `1px solid ${colors.windowBorder}`,
            borderRadius: '50%',
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.windowControlBgHover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.windowControlBg
          }}
          title="Close"
        />
        <div
          draggable={false}
          onClick={(e) => {
            e.stopPropagation()
            if (onMinimize) {
              onMinimize()
            }
          }}
          style={{
            width: '10px',
            height: '10px',
            backgroundColor: colors.windowControlBg,
            border: `1px solid ${colors.windowBorder}`,
            borderRadius: '50%',
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.windowControlBgHover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.windowControlBg
          }}
          title="Restore"
        />
        <div
          draggable={false}
          onClick={(e) => {
            e.stopPropagation()
            // Only allow maximize if canMaximize is true
            if (onMaximize && canMaximize) {
              onMaximize()
            }
          }}
          style={{
            width: '10px',
            height: '10px',
            backgroundColor: colors.windowControlBg,
            border: `1px solid ${colors.windowBorder}`,
            borderRadius: '50%',
            flexShrink: 0,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.windowControlBgHover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.windowControlBg
          }}
          title="Maximize"
        />
      </div>

      {/* Window title */}
      <div
        onClick={(e) => {
          e.stopPropagation()
          if (onMinimize) {
            onMinimize()
          }
        }}
        style={{
          color: colors.primaryText,
          fontSize: '12px',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          writingMode:
            minimizeType === 'vertical' ? 'vertical-rl' : 'horizontal-tb',
          transform: minimizeType === 'vertical' && i18n.language !== 'ja' ? 'rotate(180deg)' : 'none',
          userSelect: 'none',
          cursor: 'pointer',
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {title}
      </div>
    </div>
  )

  // Window header and content (same for both modes)
  const windowInnerContent = (
    <div
      style={{
        width: '100%',
        height: isMobile ? 'calc(100% - 40px)' : '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        className="window-header"
        style={{
          backgroundColor: colors.windowHeaderBg,
          color: colors.primaryText,
          padding: '8px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '36px',
          flexShrink: 0,
          borderTopLeftRadius: isMobile ? 0 : '6px',
          borderTopRightRadius: isMobile ? 0 : '6px',
          userSelect: 'none',
          borderBottom: `1px solid ${colors.windowHeaderBorder}`,
        }}
      >
        {/* Left side: dot controls + title, acts as the drag handle area */}
        <div
          className="window-header-drag-region"
          draggable={isTiled && !isMobile && enableReorderDrag}
          onDragStart={(e) => {
            if (isTiled && enableReorderDrag && onReorderDragStart) {
              const target = e.target as HTMLElement
              // Prevent window reordering when dragging on interactive controls
              // (e.g. the chat font-size slider in the title bar), which are
              // marked with .window-control-button.
              if (target.closest('.window-control-button')) {
                e.preventDefault()
                e.stopPropagation()
                return
              }
              e.stopPropagation()
              onReorderDragStart(id)
            }
          }}
          onDragEnd={(e) => {
            if (isTiled && onReorderDragEnd) {
              e.stopPropagation()
              onReorderDragEnd()
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1,
            minWidth: 0,
            cursor: isTiled
              ? enableReorderDrag && isDraggedForReorder
                ? 'grabbing'
                : enableReorderDrag
                  ? 'grab'
                  : 'default'
              : draggable
                ? isDragging
                  ? 'grabbing'
                  : 'grab'
                : 'default',
          }}
        >
          <div
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            style={{
              width: '10px',
              height: '10px',
              backgroundColor: colors.windowControlBg,
              border: `1px solid ${colors.windowBorder}`,
              borderRadius: '50%',
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                colors.windowControlBgHover
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.windowControlBg
            }}
            title="Close"
          ></div>
          <div
            onClick={(e) => {
              e.stopPropagation()
              if (onMinimize) {
                onMinimize()
              }
            }}
            style={{
              width: '10px',
              height: '10px',
              backgroundColor: colors.windowControlBg,
              border: `1px solid ${colors.windowBorder}`,
              borderRadius: '50%',
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                colors.windowControlBgHover
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.windowControlBg
            }}
            title="Minimize"
          ></div>
          <div
            onClick={(e) => {
              e.stopPropagation()
              // Only allow maximize if canMaximize is true
              if (onMaximize && canMaximize) {
                onMaximize()
              }
            }}
            style={{
              width: '10px',
              height: '10px',
              backgroundColor: colors.windowControlBg,
              border: `1px solid ${colors.windowBorder}`,
              borderRadius: '50%',
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                colors.windowControlBgHover
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.windowControlBg
            }}
            title="Maximize"
          ></div>
          <div
            className="window-title"
            style={{
              fontWeight: '500',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
              fontSize: '14px',
              letterSpacing: '0.2px',
            }}
          >
            {title}
          </div>
        </div>
        {titleBarActions && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}
          >
            {titleBarActions}
          </div>
        )}
      </div>
      <div
        className="window-content"
        style={{
          flexGrow: 1,
          minHeight: 0, // Critical for flex children to properly constrain height
          backgroundColor: colors.primaryBg,
          overflow: 'auto', // Change back to auto to ensure content is visible
          display: 'flex',
          flexDirection: 'column',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          pointerEvents: isDragging || isResizing ? 'none' : 'auto',
        }}
      >
        {children ? (
          children
        ) : (
          <p style={{ padding: '10px' }}>Window content for {title}</p>
        )}
      </div>
    </div>
  )

  // In tiled mode, render directly without Draggable/ResizableBox
  if (isTiled) {
    const contentToRender = isMinimizedAndTiled
      ? minimizedContent
      : windowInnerContent

    return (
      <div
        ref={draggableRef}
        className={`window-wrapper ${isActive ? 'active' : ''}`}
        data-window-id={id}
        data-content-type={contentType}
        onClick={(e) => {
          const target = e.target as HTMLElement
          if (!target.closest('.window-control-button')) {
            onFocus()
          }
        }}
        onDragOver={(e) => {
          if (!isMobile && onReorderDragOver) {
            e.preventDefault()
            onReorderDragOver(id)
          }
        }}
        onDrop={(e) => {
          if (!isMobile && onReorderDrop) {
            e.preventDefault()
            onReorderDrop(id)
          }
        }}
        style={windowStyle}
      >
        {contentToRender}
      </div>
    )
  }

  // Floating mode with dragging/resizing
  const windowContent = (
    <div
      ref={draggableRef}
      className={`window-wrapper ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
      data-window-id={id}
      data-content-type={contentType}
      onClick={(e) => {
        const target = e.target as HTMLElement
        if (
          !target.closest('.window-control-button') &&
          !target.closest('.react-resizable-handle')
        ) {
          onFocus()
        }
      }}
      style={windowStyle}
    >
      <ResizableBox
        width={isMobile ? window.innerWidth - 4 : size.width}
        height={isMobile ? window.innerHeight - 4 : size.height}
        minConstraints={[200, 150]}
        maxConstraints={[
          isMobile ? window.innerWidth : window.innerWidth - position.x,
          isMobile ? window.innerHeight - 60 : window.innerHeight - position.y,
        ]}
        onResize={handleResize}
        onResizeStart={() => setIsResizing(true)}
        onResizeStop={handleResizeStop}
        resizeHandles={isMobile ? [] : ['se']}
      >
        {windowInnerContent}
      </ResizableBox>
    </div>
  )

  if (!draggable) {
    return windowContent
  }

  return (
    <Draggable
      key="normal"
      nodeRef={draggableRef as React.RefObject<HTMLElement>}
      handle=".window-header-drag-region"
      onStart={handleDragStart}
      onDrag={handleDrag}
      onStop={handleDragStop}
      position={position}
      defaultPosition={initialPosition}
      cancel=".window-control-button, .react-resizable-handle, .text-size-control, input[type='range']"
      bounds={calculateBounds()}
    >
      {windowContent}
    </Draggable>
  )
}
